import { Component, EventEmitter, forwardRef, Input, Output } from '@angular/core'
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms'
import { IonicModule } from '@ionic/angular'
import { AsyncPipe, NgClass, NgForOf, NgIf } from '@angular/common'
import { BehaviorSubject, combineLatest, Observable } from 'rxjs'
import { AssistanceStatus, AssistanceStatusName } from '../../../../models/assistanceStatus'
import { AssistanceStatusesService } from '../../../../services/domain/codelists/assistance-statuses.service'
import { map } from 'rxjs/operators'
import { Assistance } from '../../../../models/assistance'
import { FormStatus } from '../../../../models/functional/formStatus'

@Component({
  selector: 'app-assistance-editor-assistance-status',
  templateUrl: './assistance-editor-assistance-status.component.html',
  styleUrls: ['./assistance-editor-assistance-status.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AssistanceEditorAssistanceStatusComponent),
      multi: true,
    },
  ],
  imports: [IonicModule, NgIf, AsyncPipe, NgForOf, NgClass],
})
export class AssistanceEditorAssistanceStatusComponent implements ControlValueAccessor {
  @Input() editable: boolean = false
  @Input() formStatus: FormStatus
  @Input() statusValidationInputs: { realizedFrom: Date; realizedTo: Date }

  @Output() advanceStatusRequested = new EventEmitter<void>()
  @Output() cancelAssistanceRequested = new EventEmitter<void>()

  private _value: number // statusId
  public disabled: boolean
  private _onChange: (value: number) => void
  protected _onTouched: () => void
  protected readonly AssistanceStatusName = AssistanceStatusName

  public async getCurrentStatus(): Promise<AssistanceStatus> {
    return await this.statusService.getByIdOnce(this._value)
  }

  constructor(private statusService: AssistanceStatusesService) {}

  valueChanged$$ = new BehaviorSubject<void>(null)
  valueChanged$ = this.valueChanged$$.asObservable()

  protected currentStatus$: Observable<AssistanceStatus> = combineLatest([
    this.statusService.getAll(),
    this.valueChanged$,
  ]).pipe(
    map(([statuses, _]) => {
      if (!this._value) {
        const defaultStatus = statuses.find((s) => s.name === AssistanceStatusName.PLANNED)
        this._value = defaultStatus.id
        this._onChange(this._value)
        return defaultStatus
      }
      return statuses.find((s) => s.id === this._value)
    }),
  )

  protected positiveStatuses$: Observable<AssistanceStatus[]> = this.statusService.getPositiveStatusesOrdered()

  registerOnChange(fn: any): void {
    this._onChange = fn
  }
  registerOnTouched(fn: any): void {
    this._onTouched = fn
  }
  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled
  }
  writeValue(value: any): void {
    console.log('AssistanceEditorAssistanceStatusComponent#writeValue', value)
    this._value = value
    this.valueChanged$$.next()
  }

  canShowStatusButtonCancel(status: AssistanceStatus) {
    return Assistance.canBeCanceled(
      this.statusValidationInputs?.realizedFrom,
      this.statusValidationInputs?.realizedTo,
      status,
    )
  }

  async advanceStatus() {
    this.advanceStatusRequested.emit()
  }

  async requestCancel() {
    this.cancelAssistanceRequested.emit()
  }
}
